import { IMenhirBenefitSlug } from '@/common/integrations/menhir/employee/benefits/benefits.types';
import {
  IAppleOrder,
  IDonationOrder,
  IOrder,
  IRetirementInvestmentOrder,
  TOrderCategorySlug,
} from '@/common/integrations/menhir/employee/orders/orders.types';
import {
  ICatalogProduct,
  IReimbursableBenefit,
  IReimbursableProductSlug,
  IReimbursementOrder,
  IRetirementInvestmentProduct,
} from '@/common/types/catalog';
import { IBenefitType } from '@/common/types/general';

export const isRetirementInvestmentOrder = (
  order: Pick<IOrder<TOrderCategorySlug>, 'product'>
): order is IRetirementInvestmentOrder =>
  order.product.category.slug === 'retirement-investment';

export const isRetirementInvestmentProduct = (
  product: ICatalogProduct<TOrderCategorySlug>
): product is IRetirementInvestmentProduct =>
  product.category.slug === 'retirement-investment';

export const isReimbursementOrder = (
  order?: Pick<IOrder<TOrderCategorySlug>, 'product'>
): order is IReimbursementOrder<IReimbursableProductSlug> =>
  [
    'technology-reimbursement',
    'transportation-reimbursement',
    'professional-training-reimbursement',
    'education-training-reimbursement',
    'gym-fitness-reimbursement',
    'health-wellbeing-reimbursement',
    'senior-expenses-reimbursement',
    'retirement-investment-reimbursement',
    'parking-reimbursement',
    'summer-camp-reimbursement',
    'other-benefits-reimbursement',
    'budgets-reimbursement',
    'it-education-reimbursement',
    'it-transportation-reimbursement',
    'family-care-services-reimbursement',
  ].includes(order?.product.slug ?? '');

export const ReimbursementBenefitTypeMapper: Record<
  IReimbursableBenefit,
  IBenefitType
> = {
  technology: 'technology',
  'professional-training': 'professional_training',
  transportation: 'transportation',
  'education-training': 'education_training',
  'gym-fitness': 'gym_fitness',
  'health-wellbeing': 'health_wellbeing',
  'senior-expenses': 'senior_expenses',
  'retirement-investment': 'retirement_investment',
  parking: 'parking',
  'summer-camp': 'summer_camp',
  donation: 'donation',
  'other-benefits': 'other_benefits',
  fringe: 'fringe',
  recreation: 'recreation',
  'family-care-services': 'family_care_services',
  'it-education': 'it_education',
  'it-transportation': 'it_transportation',
  'pension-funds': 'pension_funds',
};

export const ReimburseableMapper: Record<
  IReimbursableBenefit | 'budgets',
  IBenefitType | 'budgets'
> = {
  ...ReimbursementBenefitTypeMapper,
  budgets: 'budgets',
};

export const isReimbursableBenefit = (benefit: IMenhirBenefitSlug) =>
  Object.keys(ReimbursementBenefitTypeMapper).includes(benefit);

export const isAppleOrder = (
  order?: Pick<IOrder<TOrderCategorySlug>, 'product'>
): order is IAppleOrder => order?.product.slug === 'apple';

export const isDonationOrder = (
  order?: Pick<IOrder<TOrderCategorySlug>, 'product'>
): order is IDonationOrder => order?.product.slug === 'donation';
